import React, { useEffect, useRef, useState, memo } from 'react';
import { FeatureFragment } from "@graphqlTypes";
import { useInView } from "motion/react";
import { AnimVals } from '@/utils/animUtils';
import { useViewSize } from '@/providers/ViewSizeContext';
import { useDebug } from '@/utils/useDebugLog';
import { getDateRange, getLinkData } from "@/core/utils";
import { usePostType } from "@/hooks/postType";

import { Button, TypePill, Image, RevealWrapper, RhLink } from "@components";

import useParallax from "@/components/Parallax/useParallax";

import className from "classnames/bind";
import styles from "./Feature.module.scss";
const cx = className.bind(styles);

export type FeatureItemType = Omit<FeatureFragment['items'][number], '__typename'> & {
  __typename?: string;
};

type FeatureItemProps = {
  data: FeatureItemType;
  index: number;
  disableParallax?: boolean;
  single?: boolean;
  featureType?: string | 'bento' | 'columns';
};

const FeatureItem = ({ data, index, single, featureType, disableParallax }: FeatureItemProps) => {
  const { log } = useDebug({ name: 'FeatureItem_Option5', bgColor: 'blue', textColor: 'white', enabled: false });

  const { body, ctaLabel, headline, linkType, pillLabel, image, hideTypePill, page } = data;

  // Page data handling
  const pageData = page?.node;
  const postType = usePostType(page);

  // Link handling
  const [type] = linkType;
  const isExternal = type === "external";
  const pillType = isExternal ? 'external' : postType?.key;
  const parsedPillLabel = isExternal ? pillLabel : postType?.label;
  const showPill = Boolean(pillType && parsedPillLabel && !hideTypePill);
  const linkData = getLinkData(data, true);
  if (!isExternal && !pageData) {
    console.warn("No page data found for FeatureItem", page, type);
  }

  // Date handling
  const showDate = pageData && "eventData" in pageData;
  let startDate: string, endDate: string;
  if (showDate) {
    startDate = pageData.eventData.startDate;
    endDate = pageData.eventData.endDate;
  }
  let date;
  if (showDate) date = getDateRange(startDate, endDate);

  // Headline class
  const headlineClass = single ? 'title-lc-l' : 'title-lc-m';

  // -------------------- Animation
  //#region Animation

  const rootEl = useRef<HTMLDivElement>(null);
  const { isDesktop } = useViewSize();
  const REVEAL = AnimVals;

  const triggerPercent = isDesktop ? REVEAL.lg.triggerPercent : REVEAL.sm.triggerPercent;
  const isInView = useInView(rootEl, { amount: triggerPercent, once: REVEAL.once });
  const ANIM_DELAY = AnimVals.sm.delay;


  const [hasAnimatedIn, setHasAnimatedIn] = useState(false);

  // ---------- Delay

  // Delay based on featureType and index
  const isOdd = index % 2 !== 0;
  let isInViewDelay: number = 0;

  // For 'columns', delay is applied to even items.
  // Creating a left -> right staggered effect
  if (featureType === 'columns') {
    isInViewDelay = !isOdd ? 0 : ANIM_DELAY;
  }

  // For 'bento', delay is applied to odd items.
  // This is because the first item is the large "Single"
  // This also creates a left -> right staggered effect
  else if (featureType === 'bento') {
    isInViewDelay = isOdd ? 0 : ANIM_DELAY;
  }

  // Delayed in-view state based on index
  const [delayedInView, setDelayedInView] = useState(false);

  // ImageAnim ref so Button can trigger the ripple animation
  const imageAnimRef = useRef<any>(null);

  useEffect(() => {
    let timeout: NodeJS.Timeout;
    if (isInView) {
      timeout = setTimeout(() => setDelayedInView(true), isInViewDelay * 1000);
    } else {
      setDelayedInView(false);
    }
    return () => clearTimeout(timeout);
  }, [isInView, isInViewDelay]);

  // When the item becomes visible, play ripple reverse
  useEffect(() => {
    if (delayedInView && image && !hasAnimatedIn) {
      setHasAnimatedIn(true);
      const imageRevealDuration = (isDesktop) ? REVEAL.lg.duration : REVEAL.sm.duration;
      log('InView - schedule ImageAnim reverse', imageRevealDuration);
      imageAnimRef.current?.reverse?.(imageRevealDuration);
    }
  }, [delayedInView, image, hasAnimatedIn]);

  //#endregion Animation
  // -------------------- Parallax

  const parallaxDistance = isDesktop ? 56 : 24;

  log('Parallax setup', index, parallaxDistance, rootEl.current);

  useParallax({ containerRef: rootEl, start: 'top bottom', startPx: -parallaxDistance, endPx: parallaxDistance, isEnabled: !disableParallax });

  // -------------------- Render

  log('Render', index, rootEl.current);

  return (
    <div ref={rootEl}
      className={cx([
        "feature-item",
        `feature-item--${index}`,
        { single },
        "feature-item--alt", "feature-item--option5",
      ])}
    >
      <RevealWrapper isInView={delayedInView} className={cx("feature-item__reveal-wrapper")}>
        <RhLink
          href={linkData?.url || '#'}
          target={linkData?.target || undefined}
          className={cx("feature-item__link")}
        >
          <div
            className={cx(["feature-item__content-wrapper"]) }
          >
            <div className={cx("image-wrapper")}>
              {showPill && <TypePill type={pillType} className={cx("type-pill")} copy={parsedPillLabel} />}
              <div className={`${cx("image-inner-wrapper")} js-parallax`}>
                <Image image={image} />
              </div>
            </div>
            <div className={cx("content")}>

              <p className={cx([headlineClass, "sub-headline"])} dangerouslySetInnerHTML={{ __html: headline }} />

              <div className={cx("lower-content")}>

                <p className={cx("text-m", "body")}>
                  {body}
                </p>

                <div className={cx("button-row")}>
                  <Button label={ctaLabel} />
                  {date && (
                    <span className={cx("date", "pill")}>{date}</span>
                  )}
                </div>

              </div>
            </div>
          </div>
        </RhLink>
      </RevealWrapper>
    </div>
  );
};

export default memo(FeatureItem);
